import { useCallback } from "react";
import { useUpdateDesignProject } from "../api/useUpdateDesignProject";

export function useUpdateWithDeliveryEstimates() {
  const updateProject = useUpdateDesignProject();

  // Delivery estimates are fully handled by useDeliveryDateSync which
  // triggers on optimal DC stock boundary crossings and page load.
  // This hook now only wraps the design project update. useUpdateDesignProject
  // already calls notifyError before re-throwing, so no catch is needed here
  // (MN-4263).

  // Awaitable path: callers that `await` (e.g. SaveProject) receive the
  // rejecting promise and handle ProjectNotFoundRedirectError themselves.
  const mutateAsync = useCallback(
    (updateParams: Parameters<typeof updateProject.mutateAsync>[0]) =>
      updateProject.mutateAsync(updateParams),
    [updateProject]
  );

  // Fire-and-forget path: delegate to React Query's void `mutate`, which
  // settles the rejection internally (error state). Autosave / manual-save in
  // useDesignProjectSync call this WITHOUT awaiting, so routing them through
  // mutateAsync would leak an unhandled ProjectNotFoundRedirectError rejection
  // on a dead project (MN-10713). Preserving React Query's mutate/mutateAsync
  // distinction is what keeps that rejection out of Honeybadger.
  const mutate = useCallback(
    (updateParams: Parameters<typeof updateProject.mutate>[0]) =>
      updateProject.mutate(updateParams),
    [updateProject]
  );

  return {
    mutate,
    mutateAsync,
    isPending: updateProject.isPending,
    isSuccess: updateProject.isSuccess,
    isError: updateProject.isError,
    error: updateProject.error,
    data: updateProject.data,
    status: updateProject.status,
    reset: () => {
      updateProject.reset();
    }
  };
}
